import { RouteObject, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import { DASHBOARD_PATH } from 'config';
import { GuardProps, NavItemType } from 'types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'store';
import { cleanActiveFields, getUserMulticatalog } from 'store/slices/auth';
import MainRoutes from 'routes/MainRoutes';
import { getNewRouteType } from 'utils/helpers';
import { openSnackbar } from 'store/slices/snackbar';
import { useIntl } from 'react-intl';
import { AvailableMenuType } from 'types/components/shared';
import { getTenantId } from '../../store/slices/auth';
import useQueryParams from 'hooks/useQueryParams';

// ==============================|| GUEST GUARD ||============================== //

/**
 * Guest guard for routes having no auth required
 * @param {PropTypes.node} children children element/node
 */

const GuestGuard = ({ children }: GuardProps) => {
    const { isLoggedIn, user, codeVerified, logout, setCodeVerified } = useAuth();
    const { isProvider, termsAndConditions } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const dispatch = useDispatch();
    const serviceToken = window.localStorage.getItem('serviceToken');
    const [searchParams] = useSearchParams();
    const { tenantId } = useSelector((state) => state.auth);

    const oobCode: string | null = searchParams.get('oobCode');

    // Accessing a specific query parameter
    const { redirectUrl } = useQueryParams();

    useEffect(() => {
        const asyncEffect = async () => {
            if (!tenantId) {
                dispatch(getTenantId());
                return;
            }

            if (codeVerified && !user?.user) {
                return navigate({ pathname: '/loolu/admin/mfa' });
            }

            if (!isLoggedIn) {
                return;
            }

            if (isLoggedIn && isProvider && !termsAndConditions) {
                return navigate({ pathname: '/loolu/admin/terms-and-conditions' });
            }
            if (location.pathname === '/loolu/admin/suppliers') {
                return;
            }
            if (!user?.user && !oobCode) {
                return navigate({ pathname: '/loolu/admin/login' });
            }
            if (!isLoggedIn && !oobCode) {
                navigate({ pathname: '/loolu/admin/login' });
                return;
            }

            let errorCounter = 0;

            dispatch(cleanActiveFields([]));

            const getUserMenuDetails = async () => {
                if (!serviceToken) {
                    logout();
                    setCodeVerified();
                    return;
                }
                if (errorCounter >= 5) {
                    logout();
                    setCodeVerified();
                    return dispatch(
                        openSnackbar({
                            message: intl.formatMessage({
                                id: 'cannot_get_profile_menu_details'
                            }),
                            variant: 'error'
                        })
                    );
                }

                try {
                    const {
                        payload: {
                            response: {
                                profile: { menuDetails }
                            }
                        }
                    } = await dispatch(getUserMulticatalog(user?.user!));

                    const menuChildren =
                        menuDetails?.flatMap((item: AvailableMenuType) =>
                            item.children.map((child: AvailableMenuType) => getNewRouteType(child.type))
                        ) || [];

                    const filteredRoutes: RouteObject[] = MainRoutes.children!.filter((route) =>
                        menuChildren.some((menuItem: NavItemType) => route!.path!.includes(`/${menuItem}`))
                    );

                    const firstFilteredRoute = filteredRoutes[0];

                    const defaultRoute = filteredRoutes.find((route: RouteObject) => route.path === DASHBOARD_PATH) || firstFilteredRoute;

                    // usar redirectUrl si es que existe o usar un default route
                    const defaultPath = redirectUrl || defaultRoute?.path || '/';

                    return navigate(defaultPath, { replace: true });
                } catch (error) {
                    return setTimeout(() => {
                        errorCounter += 1;
                        getUserMenuDetails();
                    }, 1000);
                }
            };
            getUserMenuDetails();
        };

        asyncEffect();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, user, codeVerified]);

    return children;
};

export default GuestGuard;
